// src/api/procurementPlanApi.js
import API from "./axiosConfig";

// Utility to unwrap response data consistently
const unwrap = (res) => res.data || res;

/**
 * Normalizes field validation errors into a simple object.
 * Example: { vendorId: ["Required"] } -> { vendorId: "Required" }
 */
const normalizeFieldErrors = (fieldErrors) => {
  if (!fieldErrors) return null;
  return Object.fromEntries(
    Object.entries(fieldErrors).map(([key, val]) => [
      key,
      Array.isArray(val) ? val[0] : val,
    ])
  );
};

// Custom error class for API errors with field validation support
class ApiError extends Error {
  constructor(message, fieldErrors = null, status = null, originalError = null) {
    super(message);
    this.name = "ApiError";
    this.fieldErrors = normalizeFieldErrors(fieldErrors);
    this.status = status;
    this.originalError = originalError;
  }
}

/**
 * Creates a new procurement plan.
 */
export const createPlan = async (planData) => {
  try {
    const response = await API.post("/procurement-plans", planData);
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to create procurement plan",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error creating procurement plan:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to create procurement plan",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches all procurement plans.
 */
export const listPlans = async () => {
  try {
    const response = await API.get("/procurement-plans");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch procurement plans",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching procurement plans:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch procurement plans",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches a procurement plan by project ID.
 */
export const getPlanByProject = async (projectId) => {
  try {
    const response = await API.get(`/procurement-plans/project/${projectId}`);
    const responseData = response.data;

    if (response.status === 404) {
      return null;
    }

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch procurement plan",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return null;
    }

    console.error("Error fetching procurement plan by project ID:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch procurement plan",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Updates an existing procurement plan by its ID.
 */
export const updatePlan = async (id, updateData) => {
  try {
    const response = await API.put(`/procurement-plans/${id}`, updateData);
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to update procurement plan",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error updating procurement plan:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to update procurement plan",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Deletes a procurement plan by its ID.
 */
export const deletePlan = async (id) => {
  try {
    const response = await API.delete(`/procurement-plans/${id}`);
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to delete procurement plan",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error deleting procurement plan:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to delete procurement plan",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Submits a compliance decision for a procurement plan.
 */
export const submitComplianceDecision = async (id, complianceData) => {
  try {
    const response = await API.patch(
      `/procurement-plans/${id}/compliance`,
      complianceData
    );
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to submit compliance decision",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error submitting compliance decision:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to submit compliance decision",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches aggregated statistics for the HoC dashboard.
 */
export const getDashboardStats = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard-stats");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch dashboard stats",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching procurement dashboard stats:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch dashboard stats",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches procurement plans requiring compliance review with pagination and filtering.
 * @param {Object} params - Query parameters for filtering and pagination
 * @param {string} params.riskLevel - Filter by risk level (Low, Medium, High)
 * @param {string} params.dateFrom - Filter by start date (ISO format)
 * @param {string} params.dateTo - Filter by end date (ISO format)
 * @param {string} params.sortBy - Field to sort by (priority, createdAt)
 * @param {string} params.sortOrder - Sort order (asc, desc)
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @returns {Promise<{plans: Array, pagination: {total: number, page: number, pages: number}}>}
 */
export const listComplianceReviewPlans = async (params = {}) => {
  try {
    const response = await API.get("/procurement-plans/compliance-review", { params });
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch compliance review plans",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching compliance review plans:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch compliance review plans",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches detailed procurement plan by ID with compliance history.
 * @param {string} id - Procurement plan ID
 * @returns {Promise<Object>} Detailed plan object with populated references
 */
export const getPlanById = async (id) => {
  try {
    const response = await API.get(`/procurement-plans/${id}/details`);
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch procurement plan details",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching procurement plan details:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch procurement plan details",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Searches procurement plans with filters and pagination.
 * @param {Object} params - Query parameters for search and pagination
 * @param {string} params.q - Search term
 * @param {string} params.status - Filter by status
 * @param {string} params.method - Filter by procurement method
 * @param {string} params.risk - Filter by risk level
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @returns {Promise<{plans: Array, pagination: {total: number, page: number, pages: number}}>}
 */
export const searchPlans = async (params = {}) => {
  try {
    const response = await API.get("/procurement-plans/search", { params });
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to search procurement plans",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error searching procurement plans:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to search procurement plans",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches compliance statistics.
 * @returns {Promise<Object>} Compliance statistics data
 */
export const getComplianceStats = async () => {
  try {
    const response = await API.get("/procurement-plans/compliance-stats");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch compliance statistics",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching compliance statistics:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch compliance statistics",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches recent compliance activities.
 * @returns {Promise<Array>} Array of recent compliance activities
 */
export const getRecentComplianceActivities = async () => {
  try {
    const response = await API.get("/procurement-plans/compliance-activities");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch recent compliance activities",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching recent compliance activities:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch recent compliance activities",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

// =========================
// NEW DASHBOARD VISUALIZATION ENDPOINTS
// =========================

/**
 * Fetches procurement method distribution for dashboard.
 * @returns {Promise<Array>} Array of procurement method data with name, value, and color
 */
export const getProcurementMethodDistribution = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard/procurement-methods");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch procurement method distribution",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching procurement method distribution:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch procurement method distribution",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches risk distribution for dashboard.
 * @returns {Promise<Array>} Array of risk distribution data with name, value, and color
 */
export const getRiskDistribution = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard/risk-distribution");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch risk distribution",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching risk distribution:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch risk distribution",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches financial commitment by status for dashboard.
 * @returns {Promise<Array>} Array of financial commitment data with name, value, and color
 */
export const getFinancialCommitmentByStatus = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard/financial-commitment");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch financial commitment by status",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching financial commitment by status:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch financial commitment by status",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches plan status timeline for dashboard.
 * @returns {Promise<Array>} Array of timeline data with month and status counts
 */
export const getPlanStatusTimeline = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard/status-timeline");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch plan status timeline",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching plan status timeline:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch plan status timeline",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches priority distribution for dashboard.
 * @returns {Promise<Array>} Array of priority distribution data with name, value, and color
 */
export const getPriorityDistribution = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard/priority-distribution");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch priority distribution",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching priority distribution:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch priority distribution",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches compliance trend for dashboard.
 * @returns {Promise<Array>} Array of compliance trend data with month and rate
 */
export const getComplianceTrend = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard/compliance-trend");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch compliance trend",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching compliance trend:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch compliance trend",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Fetches risk alerts for dashboard table.
 * @returns {Promise<Array>} Array of risk alert objects
 */
export const getRiskAlerts = async () => {
  try {
    const response = await API.get("/procurement-plans/dashboard/risk-alerts");
    const responseData = response.data;

    if (responseData.success === false) {
      throw new ApiError(
        responseData.message || "Failed to fetch risk alerts",
        responseData.details || responseData.errors || null,
        response.status
      );
    }

    return unwrap(responseData);
  } catch (error) {
    console.error("Error fetching risk alerts:", error);

    if (error instanceof ApiError) throw error;

    if (error.response) {
      const errorData = error.response.data;
      throw new ApiError(
        errorData?.message || "Failed to fetch risk alerts",
        errorData?.details || errorData?.errors || null,
        error.response.status,
        error
      );
    }

    throw new ApiError(
      error.message || "Network error or server unavailable",
      null,
      null,
      error
    );
  }
};

/**
 * Utility function to handle API errors in React components.
 */
export const handleApiError = (error, setErrorMessage, setFieldErrors = null) => {
  if (error instanceof ApiError) {
    if (error.fieldErrors && setFieldErrors) {
      setFieldErrors(error.fieldErrors);
    }
    setErrorMessage(error.message);
  } else if (error.response) {
    const errorData = error.response.data;
    setErrorMessage(errorData?.message || "An unexpected error occurred");
    if (errorData?.details && setFieldErrors) {
      setFieldErrors(normalizeFieldErrors(errorData.details));
    }
  } else {
    setErrorMessage(
      error.message || "Network error. Please check your connection."
    );
  }
};

/**
 * Extracts field-level validation errors from API error.
 */
export const extractFieldErrors = (error) => {
  if (error instanceof ApiError && error.fieldErrors) {
    return error.fieldErrors;
  }
  return {};
};

const procurementPlanApi = {
  createPlan,
  listPlans,
  getPlanByProject,
  updatePlan,
  deletePlan,
  submitComplianceDecision,
  getDashboardStats,
  // Enhanced endpoints
  listComplianceReviewPlans,
  getPlanById,
  searchPlans,
  getComplianceStats,
  getRecentComplianceActivities,
  // NEW: Dashboard visualization endpoints
  getProcurementMethodDistribution,
  getRiskDistribution,
  getFinancialCommitmentByStatus,
  getPlanStatusTimeline,
  getPriorityDistribution,
  getComplianceTrend,
  getRiskAlerts,
  // Utilities
  handleApiError,
  extractFieldErrors,
  ApiError,
};

export default procurementPlanApi;